class WindCalculator {
	constructor() {
		this.cruiseSamples = [];
		this.circleSamples = [];
		this.zigzagSamples = [];

		// Configure sample limits
		this.maxCruiseSamples = 180; // 3 minutes
		this.maxCircleSamples = 360; // 6 minutes
		this.maxZigzagSamples = 300; // 5 minutes

		this.minSpeed = 2.0; // m/s
		this.maxSpeed = 75.0; // m/s

		this.circling = false;
		this.lastWind = null;
		this.lastTime = null;
	}

	addSample(gpsData, trueAirspeed, heading, bankAngle = 0) {
		const currentTime = gpsData.time;
		const groundSpeed = gpsData.speed;
		const groundTrack = gpsData.bearing;

		// Validate speed
		if (groundSpeed < this.minSpeed || groundSpeed > this.maxSpeed) {
			return;
		}

		const sample = {
			time: currentTime,
			groundSpeed: groundSpeed,
			groundTrack: groundTrack,
			trueAirspeed: trueAirspeed,
			heading: heading,
			bankAngle: bankAngle,
		};

		// Determine flight mode and store sample
		if (Math.abs(bankAngle) > 25) {
			// Circling detection
			this.circling = true;
			this.circleSamples.push(sample);
			if (this.circleSamples.length > this.maxCircleSamples) {
				this.circleSamples.shift();
			}
		} else {
			this.circling = false;
			this.cruiseSamples.push(sample);
			this.zigzagSamples.push(sample);

			if (this.cruiseSamples.length > this.maxCruiseSamples) {
				this.cruiseSamples.shift();
			}
			if (this.zigzagSamples.length > this.maxZigzagSamples) {
				this.zigzagSamples.shift();
			}
		}

		this.lastTime = currentTime;
	}

	calculateCruiseWind() {
		if (this.cruiseSamples.length < 10) {
			return null;
		}

		let sumWe = 0;
		let sumWn = 0;
		let count = 0;

		for (let i = 0; i < this.cruiseSamples.length - 1; i++) {
			const sample = this.cruiseSamples[i];

			const tas = sample.trueAirspeed;
			const headingRad = this.toRadians(sample.heading);
			const trackRad = this.toRadians(sample.groundTrack);
			const gs = sample.groundSpeed;

			// Calculate wind components
			const we = gs * Math.sin(trackRad) - tas * Math.sin(headingRad);
			const wn = gs * Math.cos(trackRad) - tas * Math.cos(headingRad);
			// console.log({sample, we, wn});
			sumWe += we;
			sumWn += wn;
			count++;
		}

		if (count === 0) return null;

		const avgWe = sumWe / count;
		const avgWn = sumWn / count;

		const windSpeed = Math.sqrt(avgWe * avgWe + avgWn * avgWn);
		let windDirection = this.toDegrees(Math.atan2(avgWe, avgWn));
		windDirection = (windDirection + 180) % 360;

		return [windSpeed, windDirection];
	}

	calculateCircleWind() {
		if (this.circleSamples.length < 30) {
			return null;
		}

		const windVectors = [];

		for (let i = 0; i < this.circleSamples.length - 1; i++) {
			const sample1 = this.circleSamples[i];
			const sample2 = this.circleSamples[i + 1];

			const tas1 = sample1.trueAirspeed;
			const tas2 = sample2.trueAirspeed;
			const hdg1 = this.toRadians(sample1.heading);
			const hdg2 = this.toRadians(sample2.heading);
			const gs1 = sample1.groundSpeed;
			const gs2 = sample2.groundSpeed;
			const trk1 = this.toRadians(sample1.groundTrack);
			const trk2 = this.toRadians(sample2.groundTrack);

			const windE =
				(gs1 * Math.sin(trk1) + gs2 * Math.sin(trk2)) / 2 -
				(tas1 * Math.sin(hdg1) + tas2 * Math.sin(hdg2)) / 2;
			const windN =
				(gs1 * Math.cos(trk1) + gs2 * Math.cos(trk2)) / 2 -
				(tas1 * Math.cos(hdg1) + tas2 * Math.cos(hdg2)) / 2;

			windVectors.push([windE, windN]);
		}

		if (windVectors.length === 0) return null;

		const avgE =
			windVectors.reduce((sum, v) => sum + v[0], 0) / windVectors.length;
		const avgN =
			windVectors.reduce((sum, v) => sum + v[1], 0) / windVectors.length;

		const windSpeed = Math.sqrt(avgE * avgE + avgN * avgN);
		let windDirection = this.toDegrees(Math.atan2(avgE, avgN));
		windDirection = (windDirection + 180) % 360;

		return [windSpeed, windDirection];
	}

	calculateZigzagWind() {
		if (this.zigzagSamples.length < 20) {
			return null;
		}

		// Check for heading variation
		const headings = this.zigzagSamples.map((s) => s.heading);
		if (Math.max(...headings) - Math.min(...headings) < 45) {
			return null;
		}

		const recentSamples = this.zigzagSamples.slice(-20);
		let sumWe = 0;
		let sumWn = 0;
		let count = 0;

		for (const sample of recentSamples) {
			const tas = sample.trueAirspeed;
			const headingRad = this.toRadians(sample.heading);
			const trackRad = this.toRadians(sample.groundTrack);
			const gs = sample.groundSpeed;

			const we = gs * Math.sin(trackRad) - tas * Math.sin(headingRad);
			const wn = gs * Math.cos(trackRad) - tas * Math.cos(headingRad);

			sumWe += we;
			sumWn += wn;
			count++;
		}

		if (count === 0) return null;

		const avgWe = sumWe / count;
		const avgWn = sumWn / count;

		const windSpeed = Math.sqrt(avgWe * avgWe + avgWn * avgWn);
		let windDirection = this.toDegrees(Math.atan2(avgWe, avgWn));
		windDirection = (windDirection + 180) % 360;

		return [windSpeed, windDirection];
	}

	getWind() {
		if (this.circling) {
			const wind = this.calculateCircleWind();
			if (wind) return { wind, method: "circle" };
		}

		const zigzagWind = this.calculateZigzagWind();
		if (zigzagWind) return { wind: zigzagWind, method: "zigzag" };

		const cruiseWind = this.calculateCruiseWind();
		if (cruiseWind) return { wind: cruiseWind, method: "cruise" };

		return { wind: null, method: null };
	}

	// Helper functions
	toRadians(degrees) {
		return (degrees * Math.PI) / 180;
	}

	toDegrees(radians) {
		return (radians * 180) / Math.PI;
	}
}

export function calculateWind(gpsDataArray, metarWind) {
	try {
		const windCalc = new WindCalculator();

		const enhancedGPSData = [];
		const windResults = [];

		let lastHeading = null;
		let headingChange = 0;
		let sampleCount = 0;
		const HEADING_THRESHOLD = 45; // Minimum heading change for zigzag
		const SAMPLE_THRESHOLD = 10; // Minimum samples needed for calculation

		gpsDataArray.forEach((gpsData, index) => {
			// Basic validation
			if (!isValidGPSData(gpsData)) {
				return;
			}

			const currentHeading = gpsData.bearing;

			// Track heading changes
			if (lastHeading !== null) {
				const change = Math.abs(currentHeading - lastHeading);
				headingChange = Math.max(change, headingChange);
			}
			lastHeading = currentHeading;
			sampleCount++;

			// Calculate true airspeed (you might want to implement your own method)
			const trueAirspeed = calculateTAS(gpsData);

			// Detect bank angle from heading change rate
			const bankAngle = estimateBankAngle(
				gpsData,
				index > 0 ? gpsDataArray[index - 1] : null,
			);

			// Add sample to wind calculator
			windCalc.addSample(gpsData, trueAirspeed, currentHeading, bankAngle);

			// Check conditions for wind calculation
			let windCalculation = null;
			let flightCondition = "cruise";
			if (sampleCount >= SAMPLE_THRESHOLD) {
				if (Math.abs(bankAngle) > 25) {
					// Circling condition
					windCalculation = windCalc.calculateCircleWind();
					flightCondition = "circle";
				} else if (headingChange >= HEADING_THRESHOLD) {
					// Zigzag condition
					windCalculation = windCalc.calculateZigzagWind();
					flightCondition = "zigzag";
					headingChange = 0; // Reset heading change after calculation
				} else {
					// Cruise condition
					windCalculation = windCalc.calculateCruiseWind();
					flightCondition = "cruise";
				}
			}

			let windInfo = null;
			// console.log('windCalculation', windCalculation)
			if (windCalculation) {
				const windMerged = calculateWindWithMetar(
					windCalculation,
					metarWind,
					flightCondition,
				);

				// console.log('windMerged', windMerged)

				windInfo = {
					timestamp: gpsData.time,
					windSpeed: windMerged.windSpeed,
					windDirection: windMerged.windDirection,
					flightCondition,
				};
				windResults.push(windInfo);
			}

			// Create enhanced GPS data entry
			const enhancedData = {
				...gpsData,
				metarWind,
				calculatedData: {
					trueAirspeed,
					bankAngle,
					flightCondition,
					// wind: windInfo,
					// As we now only use wind from METAR, we can use the wind from the METAR
					wind: {
						windSpeed: metarWind.speed,
						windDirection: Number(metarWind.direction) || 0,
					},
				},
			};

			enhancedGPSData.push(enhancedData);
		});

		return {
			metadata: {
				processedAt: new Date().toISOString(),
				totalSamples: enhancedGPSData.length,
				windCalculations: windResults.length,
			},
			enhancedGPSData,
			windResults,
		};
	} catch (error) {
		// eslint-disable-next-line no-console
		console.log(error);
		return {
			enhancedGPSData: gpsDataArray,
		};
	}
}

// Helper Functions
function isValidGPSData(gpsData) {
	return (
		gpsData &&
		typeof gpsData.speed === "number" &&
		typeof gpsData.bearing === "number" &&
		typeof gpsData.time === "number" &&
		typeof gpsData.latitude === "number" &&
		typeof gpsData.longitude === "number" &&
		gpsData.speed >= 0 &&
		gpsData.speed < 100 && // reasonable speed limit
		gpsData.latitude >= -90 &&
		gpsData.latitude <= 90 &&
		gpsData.longitude >= -180 &&
		gpsData.longitude <= 180
	);
}

function calculateTAS(gpsData, metarWind) {
	// Basic TAS calculation - could be enhanced with temperature/pressure data
	const groundSpeed = gpsData.speed;
	if (!metarWind) return groundSpeed * 1.1; // Simple estimation

	// More accurate calculation using wind triangle
	const trackRad = (gpsData.bearing * Math.PI) / 180;
	const windDirRad = (metarWind.direction * Math.PI) / 180;
	const windSpeed = metarWind.speed;

	const windCross = windSpeed * Math.sin(windDirRad - trackRad);
	const windAlong = windSpeed * Math.cos(windDirRad - trackRad);

	return Math.sqrt(
		Math.pow(groundSpeed - windAlong, 2) + Math.pow(windCross, 2),
	);
}

function estimateBankAngle(currentGPS, previousGPS) {
	if (!previousGPS) return 0;

	const timeStep = (currentGPS.time - previousGPS.time) / 1000; // seconds
	if (timeStep === 0) return 0;

	// Calculate turn rate
	const headingChange = Math.abs(currentGPS.bearing - previousGPS.bearing);
	const turnRate = headingChange / timeStep;

	// Estimate bank angle using turn rate
	// Simplified formula: bank angle ≈ arctan(turn rate * TAS / g)
	const g = 9.81; // m/s²
	const tas = calculateTAS(currentGPS);
	return Math.atan((turnRate * tas) / g) * (180 / Math.PI);
}

function calculateWindWithMetar(calculatedWind, metarWind, flightCondition) {
	let method = "";
	let confidence = 0;

	// Get wind calculation based on flight condition
	switch (flightCondition) {
		case "circling":
			method = "circle";
			confidence = 0.8;
			break;
		case "zigzag":
			method = "zigzag";
			confidence = 0.7;
			break;
		default:
			method = "cruise";
			confidence = 0.6;
	}
	// Blend with METAR data if available
	if (metarWind) {
		const blendedWind = blendWindData(
			calculatedWind[0],
			calculatedWind[1],
			metarWind.speed,
			metarWind.direction,
			confidence,
		);

		return {
			windSpeed: blendedWind.speed,
			windDirection: blendedWind.direction,
			method: `${method}_metar_blend`,
			confidence: blendedWind.confidence,
		};
	}

	return {
		windSpeed: calculatedWind[0],
		windDirection: calculatedWind[1],
		method,
		confidence,
	};
}

function blendWindData(
	calcSpeed,
	calcDir,
	metarSpeed,
	metarDir,
	calcConfidence,
) {
	if (calcSpeed === 0 && metarSpeed === 0) {
		return {
			speed: 0,
			direction: 0,
			confidence: 0,
		};
	}

	const metarWeight = 0.3;
	const calcWeight = calcConfidence * (1 - metarWeight);

	// Blend speeds
	const speed =
		(calcSpeed * calcWeight + metarSpeed * metarWeight) /
		(calcWeight + metarWeight);

	// Blend directions using vector components to handle circular nature
	const calcRadians = (calcDir * Math.PI) / 180;
	const metarRadians = (metarDir * Math.PI) / 180;

	const x =
		calcWeight * Math.cos(calcRadians) + metarWeight * Math.cos(metarRadians);
	const y =
		calcWeight * Math.sin(calcRadians) + metarWeight * Math.sin(metarRadians);

	let direction = (Math.atan2(y, x) * 180) / Math.PI;
	direction = (direction + 360) % 360;

	return {
		speed,
		direction,
		confidence: Math.min(calcConfidence * (1 + metarWeight), 1.0),
	};
}
